import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { filter, forkJoin, Observable, takeUntil, tap } from 'rxjs';
import {
    MatNativeDateModule,
    provideNativeDateAdapter,
} from '@angular/material/core';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { IMaskModule } from 'angular-imask';
import { IMaskConstants } from '../../../../core/constants/imask';
import { PhoneNumberInputComponent } from '../../../../shared/components/form-components/phone-number-input/phone-number-input.component';
import { ActivatedRoute, Router } from '@angular/router';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { FrnFilingDetailRequest } from './models/frn-filing-detail-request';
import { FrnFilingDetailResponse } from './models/frn-filing-detail-response';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { FrnFilingPageBaseComponent } from '../shared/components/frn-filing-page-base/frn-filing-page-base.component';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { FormEntryService } from '../../../../core/services/form-entry/form-entry.service';
import { StepNavigationService } from '../../shared/services/step-navigation/step-navigation.service';
import { ApplicationFormControlManagerService } from '../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { FrnFilingService } from '../shared/services/frn-filing.service';
import { CoresRegistrationTypeCodes } from '../shared/constants/cores-registration-type-codes';
import { HoverTooltipComponent } from '../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';

import { MatDialog } from '@angular/material/dialog';
import { ReturnRouteService } from '../../../../core/services/navigation/return-route.service';
import { formatDateToYYYYMMDD } from '../../../../core/functions/date-helpers';

@Component({
    selector: 'app-frn-filing-detail',
    templateUrl: './frn-filing-detail.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatExpansionModule,
        MatDatepickerModule,
        MatNativeDateModule,
        RegexInputDirective,
        IMaskModule,
        PhoneNumberInputComponent,
        HoverTooltipComponent,
    ],
    providers: [provideNativeDateAdapter()],
})
export class FrnFilingDetailComponent
    extends FrnFilingPageBaseComponent<
        FrnFilingDetailRequest,
        FrnFilingDetailResponse,
        FrnFilingDetailResponse
    >
    implements OnInit, OnDestroy
{
    regExPattern = RegExPattern;
    maskOptions = IMaskConstants;
    form = new FormGroup({
        coresRegistrationType: new FormControl<string | null>(null),

        // General info
        salutation: new FormControl<string | null>(null),
        firstName: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(20),
        ]),
        middleInitial: new FormControl<string | null>(
            null,
            Validators.maxLength(1),
        ),
        lastName: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(20),
        ]),
        suffix: new FormControl<string | null>(null, Validators.maxLength(3)),
        entityType: new FormControl<number | null>(null, Validators.required),
        businessEntityType: new FormControl<number | null>(
            null,
            Validators.required,
        ),
        businessEntityName: new FormControl<string | null>(null, [
            Validators.maxLength(200),
            Validators.required,
        ]),
        taxpayerIdentificationNumber: new FormControl<string | null>(
            null,
            Validators.maxLength(9),
        ),
        taxpayerIdExceptionReasonCode: new FormControl<number | null>(null),

        // Representative contact info
        contactRepresentativeTitle: new FormControl<string | null>(
            null,
            Validators.maxLength(80),
        ),
        contactRepresentativeFirstName: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(20),
        ]),
        contactRepresentativeMiddleInitial: new FormControl<string | null>(
            null,
            Validators.maxLength(1),
        ),
        contactRepresentativeLastName: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(20),
        ]),
        contactRepresentativeStreetAddress: new FormControl<string | null>(
            null,
            [Validators.required, Validators.maxLength(60)],
        ),
        // PO Box field
        contactRepresentativePoBox: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(20),
        ]),
        contactRepresentativeCity: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(20),
        ]),
        contactRepresentativeState: new FormControl<string | null>(
            null,
            Validators.required,
        ),
        contactRepresentativeZipCode: new FormControl<string | null>(null, [
            Validators.required,
        ]),
        contactRepresentativePhone: new FormControl<string | null>(
            null,
            Validators.required,
        ),
        contactRepresentativeFax: new FormControl<string | null>(null),
        contactRepresentativeEmail: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(50),
        ]),
        applicantSignature: new FormControl<string | null>(null, [
            Validators.required,
            Validators.maxLength(40),
        ]),
        signatureDate: new FormControl<string | null>(null),

        // Security info (Currently disabled but kept for future use when security info is needed)
        password: new FormControl<string | null>(
            { value: null, disabled: true },
            [Validators.minLength(8), Validators.maxLength(64)],
        ),
        confirmPassword: new FormControl<string | null>(
            { value: null, disabled: true },
            [Validators.minLength(8), Validators.maxLength(64)],
        ),
        securityQuestion: new FormControl<string | null>({
            value: null,
            disabled: true,
        }),
        securityQuestionAnswer: new FormControl<string | null>({
            value: null,
            disabled: true,
        }),
    }) as FormGroup<FormGroupType<FrnFilingDetailResponse>>;

    salutationOptions: DropdownOption[] = [];
    taxpayerIdExceptionReasonOptions: DropdownOption[] = [];
    securityQuestionOptions: DropdownOption[] = [];
    stateOptions: DropdownOption[] = [];
    entityTypeOptions: DropdownOption[] = [];
    businessEntityTypeOptions: DropdownOption[] = [];

    private readonly dropdownService = inject(DropdownService);

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(StepNavigationService),
            inject(Router),
            inject(FormEntryService),
            inject(ApplicationStateService),
            inject(ApplicationFormControlManagerService),
            inject(FrnFilingService),
            inject(MatDialog),
            inject(ReturnRouteService),
        );
    }

    /**
     * Whether the CoresRegistrationType is Individual
     *
     * @return True if the CoresRegistrationType is individual; otherwise, false.
     */
    get isIndividualCoresRegistrationType(): boolean {
        return (
            this.form.controls.coresRegistrationType?.value ===
            CoresRegistrationTypeCodes.individual
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
        this.setupFormHandlers();
        this.validatePasswords();
    }

    protected bindForm(response: FrnFilingDetailResponse): void {
        this.form.patchValue(response);
    }
    protected generateRequestObject(): FrnFilingDetailRequest {
        const formValue = this.form.getRawValue();
        const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            coresRegistrationType,
            signatureDate,
            ...requestData
        } = formValue;

        const formattedSignatureDate = signatureDate
            ? formatDateToYYYYMMDD(signatureDate)
            : null;

        return {
            ...requestData,
            signatureDate:
                formattedSignatureDate !== 'Invalid Date'
                    ? formattedSignatureDate
                    : null,
        } as FrnFilingDetailRequest;
    }

    protected getFormDataFromApi(
        applicationId: string,
    ): Observable<FrnFilingDetailResponse> {
        return this.frnFilingService.getFrnFilingDetail(applicationId);
    }

    protected saveFormDataToApi(
        request: FrnFilingDetailRequest,
    ): Observable<StepValidationResult | null> {
        if (!this.applicationId) {
            throw new Error(
                'Application ID is required to save FRN filing detail',
            );
        }

        return this.frnFilingService.updateFrnFilingDetail(
            this.applicationId,
            request,
        );
    }

    private loadDropdownData(): void {
        forkJoin({
            entityTypeOptions: this.dropdownService.getEntityTypeOptions(),
            businessEntityTypeOptions:
                this.dropdownService.getBusinessEntityTypeOptions(),
            salutationOptions: this.dropdownService.getSalutationOptions(),
            stateOptions: this.dropdownService.getStateCodes(),
            taxpayerIdExceptionReasonOptions:
                this.dropdownService.getTaxpayerIdExceptionReasonOptions(),
            securityQuestionOptions:
                this.dropdownService.getSecurityQuestionOptions(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    ({
                        entityTypeOptions,
                        businessEntityTypeOptions,
                        salutationOptions,
                        stateOptions,
                        taxpayerIdExceptionReasonOptions,
                        securityQuestionOptions,
                    }) => {
                        this.entityTypeOptions = entityTypeOptions;
                        this.businessEntityTypeOptions =
                            businessEntityTypeOptions;
                        this.salutationOptions = salutationOptions;
                        this.stateOptions = stateOptions;
                        this.taxpayerIdExceptionReasonOptions =
                            taxpayerIdExceptionReasonOptions;
                        this.securityQuestionOptions = securityQuestionOptions;
                    },
                ),
            )
            .subscribe();
    }

    private setupFormHandlers(): void {
        this.getAddressFieldMaps().forEach((field) => {
            this.form
                .get(field.main)
                ?.valueChanges.pipe(
                    takeUntil(this.destroy$),
                    filter(
                        // Type-narrowing: field.main could be any form field (including Date fields),
                        // but our address toggling is only on string fields specifically
                        (value): value is string | null =>
                            typeof value === 'string' || value === null,
                    ),
                )
                .subscribe((value) =>
                    this.togglePOBoxAndAddressFields(value, field.toggle),
                );
        });
        this.setupPasswordHandlers();
    }

    /**
     * Gets the mapping of address fields to their corresponding toggle fields.
     *
     * @returns Array of objects containing main and toggle field keys.
     */
    private getAddressFieldMaps(): Array<{
        main: keyof FormGroupType<FrnFilingDetailRequest>;
        toggle: keyof FormGroupType<FrnFilingDetailRequest>;
    }> {
        return [
            {
                main: 'contactRepresentativePoBox',
                toggle: 'contactRepresentativeStreetAddress',
            },
            {
                main: 'contactRepresentativeStreetAddress',
                toggle: 'contactRepresentativePoBox',
            },
        ];
    }

    /**
     * Toggles the enabled/disabled state and required validator of the specified form field
     * between Street Address and PO Box.
     *
     * @param value value of the main field.
     * @param fieldToToggle field being toggled.
     */
    private togglePOBoxAndAddressFields(
        value: string | null,
        fieldToToggle: keyof FormGroupType<FrnFilingDetailRequest>,
    ): void {
        const control = this.form.get(fieldToToggle);
        if (!control) {
            return;
        }

        if (value && value.trim().length > 0) {
            this.applicationFormControlManagerService.disable(
                control,
                { emitEvent: false },
                () => {
                    control.removeValidators(Validators.required);
                    control.updateValueAndValidity({ emitEvent: false });
                },
            );
        } else {
            this.applicationFormControlManagerService.enable(
                control,
                { emitEvent: false },
                () => {
                    control.addValidators(Validators.required);
                    control.updateValueAndValidity({ emitEvent: false });
                },
            );
        }
    }

    private setupPasswordHandlers(): void {
        const { password, confirmPassword } = this.form.controls;

        if (!password || !confirmPassword) {
            return;
        }

        password.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.validatePasswords());

        confirmPassword.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.validatePasswords());
    }

    /**
     * Validates that the password and confirm password fields match.
     * If they do not match, sets a 'passwordMismatch' error on the confirm password field.
     */
    private validatePasswords(): void {
        const passwordControl = this.form.controls.password;
        const confirmControl = this.form.controls.confirmPassword;

        const password = passwordControl.value;
        const confirmPassword = confirmControl.value;

        confirmControl.setErrors(null);

        // If either field is empty, do not validate
        if (!password || !confirmPassword) {
            return;
        }

        if (password !== confirmPassword) {
            confirmControl.setErrors({ passwordMismatch: true });
        }
    }
}
